import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Dropdown } from 'components/Dropdown'
import { Sorter } from 'components/Sorter/Sorter'
import {
  COMMONS_ROLES_OPTIONS,
  COMMUNITY_ROLES_OPTIONS,
  ROLES_NAMES,
  USER_STATUSES_OPTIONS,
  VENDOR_ROLES_OPTIONS
} from 'constants/common'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import {
  TContractCategoriesUsersColumnsFunc,
  TContractCategoriesUsersData,
  TContractCategoriesUsersFilters,
  TContractCategoriesUsersFiltersFunc
} from './types'
import {
  fetchHealthSystemsList,
  fetchHospitalsList,
  getVendorFilterOptions
} from '../api'
import { CellWithSubtitle } from '../../../components/CellWithSubtitle/CellWithSubtitle'
import {
  Status,
  USER_STATUS_TYPES_TO_CLASSNAME
} from '../../../components/Status'
import IconNameItem from '../../../components/IconNameItem/IconNameItem'
import { optionFormatter } from '../../Contracts/helpers'
import { Chips } from 'components/Chips/Chips'
import { Tooltip } from 'components/Tooltip/Tooltip'

export const columns = ({
  applyFilters,
  filters,
  ccId,
  isVendorTab,
  isVendorUser,
  isAutoCreated,
  canSeeHealthSystem
}: TContractCategoriesUsersColumnsFunc): ColumnsType<TContractCategoriesUsersData> => {
  const _columns: ColumnsType<TContractCategoriesUsersData> = []
  if (!isVendorUser && isVendorTab) {
    _columns.push({
      dataIndex: 'vendor',
      key: 'vendor',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="vendor_name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Vendor
        </Sorter>
      ),
      render: (_, { vendor_name }) => `${vendor_name}`,
      sorter: true,
      showSorterTooltip: false,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractCategoriesUsersFilters,
          TContractCategoriesUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getVendorFilterOptions(ccId)}
          field="vendors"
          optionFormatter={optionFormatter}
          asyncSearch
          {...props}
        />
      ),
      filterIcon: filters?.vendors?.length ? <FilterApplied16 /> : <Filter16 />,
      filterSearch: true
    })
  }
  if (!isVendorTab) {
    _columns.push({
      dataIndex: 'full_name',
      key: 'full_name',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="full_name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          User name
        </Sorter>
      ),
      render: (_, { first_name, last_name, is_voting_panel_member }) => (
        <div className="flex column">
          <div>
            {first_name} {last_name}
          </div>
          {is_voting_panel_member && isAutoCreated && (
            <div className="fit-content">
              <Tooltip title="Indicates if the Hospital user is authorized to vote in this category. Can be modified only within the Hospital user profile.">
                <Chips closable={false} grey>
                  Voting panel
                </Chips>
              </Tooltip>
            </div>
          )}
        </div>
      ),
      sorter: true,
      showSorterTooltip: false
    })
  } else {
    _columns.push({
      dataIndex: 'full_name',
      key: 'full_name',
      className: 'vendor-user-column',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="full_name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Name
        </Sorter>
      ),
      render: (_, { first_name, last_name, avatar_logo }) => (
        <IconNameItem
          firstName={first_name}
          className="vendor-user"
          lastName={last_name}
          src={avatar_logo?.file_preview || null}
          roleName={''}
        />
      ),
      sorter: true,
      showSorterTooltip: false
    })
  }

  _columns.push(
    {
      dataIndex: 'role',
      key: 'role',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractCategoriesUsersFilters,
          TContractCategoriesUsersFiltersFunc,
          unknown
        >
          items={
            isVendorTab
              ? VENDOR_ROLES_OPTIONS
              : [...COMMUNITY_ROLES_OPTIONS, ...COMMONS_ROLES_OPTIONS]
          }
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="roles"
          {...props}
        />
      ),
      filterIcon: !!filters?.roles?.length ? <FilterApplied16 /> : <Filter16 />,
      filterSearch: true,
      render: (text) => ROLES_NAMES[text],
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractCategoriesUsersFilters,
          TContractCategoriesUsersFiltersFunc,
          unknown
        >
          items={USER_STATUSES_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status }) => (
        <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
          {status}
        </Status.Default>
      ),
      filterIcon: !!filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: '195px',
      title: 'Email',
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="phone_number"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Phone
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    }
  )

  if (!isVendorTab) {
    if (canSeeHealthSystem) {
      _columns.push({
        dataIndex: 'health_system',
        key: 'health_system',
        width: '195px',
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<TContractCategoriesUsersData>
            dataIndex="health_system"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Health system
          </Sorter>
        ),
        filterDropdown: (props) => (
          <Dropdown.Filter<
            TContractCategoriesUsersFilters,
            TContractCategoriesUsersFiltersFunc,
            unknown
          >
            applyFilters={applyFilters}
            appliedFilters={filters}
            searchCallback={() => fetchHealthSystemsList(ccId, {})}
            field="health_systems"
            asyncSearch
            {...props}
          />
        ),
        render: (_, { health_system }) => (
          <CellWithSubtitle title={health_system || '-'} />
        ),
        filterIcon: !!filters?.health_systems?.length ? (
          <FilterApplied16 />
        ) : (
          <Filter16 />
        ),
        filterSearch: true,
        sorter: true,
        showSorterTooltip: false
      })
    }
    _columns.push({
      dataIndex: 'hospital',
      key: 'hospital',
      width: '195px',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TContractCategoriesUsersData>
          dataIndex="hospital"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Hospital
        </Sorter>
      ),
      render: (_, { hospital }) => <CellWithSubtitle title={hospital || '-'} />,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          TContractCategoriesUsersFilters,
          TContractCategoriesUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => fetchHospitalsList(ccId, {})}
          field="hospitals"
          asyncSearch
          {...props}
        />
      ),
      filterIcon: !!filters?.hospitals?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    })
  }

  return _columns
}
